import config from 'constants/config'
import getObjectKey from 'lib/object/getObjectKey'

export interface PassportCardQuoteParams {
  travellersDetails: Array<App.PassportCardTravellerDetail>
  destinationCountries: Array<string>
  tripStartDate: string
  tripEndDate: string
  productId: string
}

export interface PassportCardQuoteRequestParams extends PassportCardQuoteParams {
  totalPrice: number
  countryCode: string
  currency: string
  sessionId: string
  items: Array<App.PassportCardQuoteItem>
  filters?: App.PassportCardQuotePayload['filters']
  optionalCovers?: Array<App.PassportCardOptionalCover>
  loyaltyTier?: string
  spoofingAgentId?: string
}

export function hasRequiredPassportCardQuoteRequestFields(
  requestParams: PassportCardQuoteRequestParams | undefined,
): requestParams is PassportCardQuoteRequestParams {
  if (!requestParams) return false

  return (
    requestParams.travellersDetails.length > 0 &&
    requestParams.travellersDetails.every(traveller => !!traveller.dateOfBirth) &&
    requestParams.destinationCountries.length > 0 &&
    !!requestParams.tripStartDate &&
    !!requestParams.tripEndDate &&
    requestParams.totalPrice > 0 &&
    !!requestParams.sessionId &&
    !!requestParams.productId
  )
}

export function toPassportCardQuotePayload(
  requestParams: PassportCardQuoteRequestParams,
): App.PassportCardQuotePayload {
  return {
    brand: config.BRAND,
    country_code: requestParams.countryCode,
    currency: requestParams.currency,
    trip_start_date: requestParams.tripStartDate,
    trip_end_date: requestParams.tripEndDate,
    travellers_details_v2: requestParams.travellersDetails.map(t => ({
      type: t.type === 'adult' ? 'adult' : 'children',
      date_of_birth: t.dateOfBirth,
    })),
    destination_countries: requestParams.destinationCountries,
    provider: 'passport_card',
    total_price: requestParams.totalPrice,
    session_id: requestParams.sessionId,
    product_id: requestParams.productId,
    items: requestParams.items.map(item => ({ ...item })),
    ...(requestParams.filters ? { filters: requestParams.filters } : {}),
    ...(requestParams.optionalCovers !== undefined
      ? { optional_covers: requestParams.optionalCovers }
      : {}),
    ...(requestParams.loyaltyTier ? { loyalty_tier: requestParams.loyaltyTier } : {}),
    ...(requestParams.spoofingAgentId
      ? { metadata: { spoofing_agent: requestParams.spoofingAgentId } }
      : {}),
  }
}

export function getPassportCardQuoteRequestKey(
  requestParams: PassportCardQuoteRequestParams,
): string {
  const { optionalCovers: _optionalCovers, ...cacheKeyParams } = requestParams

  return getObjectKey(cacheKeyParams)
}

export function updatePassportCardOptionalCovers(
  optionalCovers: Array<App.PassportCardOptionalCover> | undefined,
  benefitCode: string,
  selected: boolean,
): Array<App.PassportCardOptionalCover> {
  const optionalCoverByCode = new Map(
    (optionalCovers ?? []).map(optionalCover => [optionalCover.code, optionalCover]),
  )
  optionalCoverByCode.set(benefitCode, { code: benefitCode, selected })

  return Array.from(optionalCoverByCode.values())
}

export function getPassportCardSelectedOptionalBenefitCodes(
  optionalBenefits: Array<App.PassportCardOptionalBenefit>,
): Array<string> {
  return optionalBenefits
    .filter(benefit => benefit.selected ?? benefit.preSelected)
    .map(benefit => benefit.code)
}

export function travellerDetailsFromInsuranceTravellers(
  travellers: Array<App.PassportCardInsuranceTraveller>,
): Array<App.PassportCardTravellerDetail> {
  return travellers.map(traveller => ({
    type: traveller.ageRange,
    dateOfBirth: traveller.dateOfBirth || '',
  }))
}

export function travellerDetailsFromOccupants(occupants: App.Occupants): Array<App.PassportCardTravellerDetail> {
  const adults = Array.from(
    { length: occupants.adults || 0 },
    () => ({ type: 'adult' as const, dateOfBirth: '' }),
  )
  const childrenCount = (occupants.children || 0) + (occupants.infants || 0)
  const children = Array.from(
    { length: childrenCount },
    () => ({ type: 'child' as const, dateOfBirth: '' }),
  )

  return [...adults, ...children]
}
